"use client";

import { ArrowDown01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

interface DatePickerProps {
	value: string | null | undefined;
	onChange: (value: string | null) => void;
	placeholder?: string;
	className?: string;
	id?: string;
	fromDate?: Date;
	toDate?: Date;
	"aria-invalid"?: boolean;
	"data-invalid"?: boolean;
	"data-warning"?: boolean;
}

export function DatePicker({
	value,
	onChange,
	placeholder = "Vyberte datum",
	className,
	id,
	fromDate,
	toDate,
	"aria-invalid": ariaInvalid,
	"data-invalid": dataInvalid,
	"data-warning": dataWarning,
}: DatePickerProps) {
	const [open, setOpen] = React.useState(false);
	const [timeZone, setTimeZone] = React.useState<string | undefined>(undefined);

	React.useEffect(() => {
		setTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
	}, []);

	const dateValue = React.useMemo(() => {
		if (!value) return undefined;
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? undefined : date;
	}, [value]);

	const today = React.useMemo(() => {
		const date = new Date();
		date.setHours(0, 0, 0, 0);
		return date;
	}, []);

	const startDate = fromDate ?? today;
	const endDate = React.useMemo(() => {
		if (toDate) return toDate;
		const date = new Date();
		date.setFullYear(date.getFullYear() + 100);
		date.setMonth(11);
		date.setDate(31);
		return date;
	}, [toDate]);

	const handleSelect = (date: Date | undefined) => {
		onChange(date?.toISOString().split("T")[0] ?? null);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				render={
					<Button
						variant="outline"
						id={id}
						className={`w-full justify-between font-normal ${className ?? ""}`}
						aria-invalid={ariaInvalid || dataInvalid}
						data-invalid={dataInvalid}
						data-warning={dataWarning}
					/>
				}
			>
				{dateValue ? (
					dateValue.toLocaleDateString("cs-CZ")
				) : (
					<span
						className={
							dataWarning ? "text-warning/70" : "text-muted-foreground"
						}
					>
						{placeholder}
					</span>
				)}
				<HugeiconsIcon icon={ArrowDown01Icon} strokeWidth={2} />
			</PopoverTrigger>
			<PopoverContent className="w-auto overflow-hidden p-0" align="start">
				<Calendar
					mode="single"
					selected={dateValue}
					captionLayout="dropdown"
					onSelect={handleSelect}
					timeZone={timeZone}
					defaultMonth={dateValue ?? today}
					startMonth={startDate}
					endMonth={endDate}
					disabled={[
						...(fromDate ? [{ before: fromDate }] : [{ before: today }]),
						...(toDate ? [{ after: toDate }] : []),
					]}
				/>
			</PopoverContent>
		</Popover>
	);
}
